/**
 * Classificação de cobertura do Capital Global para EXIBIÇÃO (resumo da
 * cotação/proposta e apólice), espelhando o que a tela de seleção de coberturas
 * faz.
 *
 * O produto usa três formatos, e o que muda entre eles é DE ONDE sai o nome:
 *
 *  1. **Contêiner base** — `093_GLB_ASS` ("Assistências") e `093_GLB_CAP`
 *     ("Capitalização"), sempre com capital 0. Aqui os BENEFÍCIOS são os itens
 *     (`676102` = "Assistência Cesta Básica…"). Na seleção são os toggles
 *     individuais (benefício sem `groupCode`).
 *
 *  2. **Variante numerada** — `093_GLB_ASS_02` … `_05`. O nome está na PRÓPRIA
 *     cobertura ("Assistência Funeral Familiar") e o benefício é só o nível de
 *     capital ("R$ 7.000"). Na seleção é o select único "Assistência Funeral"
 *     (benefícios com `groupCode`, mutuamente exclusivos).
 *
 *  3. **Cobertura com níveis** — `9300026` ("Auxílio Funeral Familiar"),
 *     `8200020`. Idem: nome na cobertura, benefício = valor. Na seleção é uma
 *     linha normal da tabela, com o select do benefício no lugar do capital.
 *
 * O código anterior classificava por `endsWith('ASS')` e depois listava os
 * benefícios de tudo que casasse. Com isso (2) não casava (o sufixo `_03` quebra
 * o `endsWith`) e (3) era descartada da tabela pelo filtro `isFinalLevelCt === 'Y'`
 * — as três funerárias sumiam das telas. E, se (2) casasse, apareceria "R$ 7.000"
 * como se fosse o nome do item.
 */

const NUMBERED_ASSISTANCE = /_ASS_\d+$/;
const NUMBERED_CAPITALIZATION = /_CAP_\d+$/;

function normalize(code?: string): string {
  return (code ?? '').trim().toUpperCase();
}

/**
 * Contêiner base, cujos BENEFÍCIOS são os itens a listar.
 * (`ASSIST` é o literal equivalente em outros produtos.)
 */
export function isCapitalGlobalAssistanceUmbrella(code?: string): boolean {
  const upper = normalize(code);
  return upper === 'ASSIST' || upper.endsWith('ASS');
}

export function isCapitalGlobalCapitalizationUmbrella(code?: string): boolean {
  const upper = normalize(code);
  return upper === 'CAPITA' || upper.endsWith('CAP');
}

/**
 * Variante numerada de assistência: o item é a PRÓPRIA cobertura — o benefício
 * aninhado é o nível de capital, não o nome.
 */
export function isCapitalGlobalAssistanceVariant(code?: string): boolean {
  return NUMBERED_ASSISTANCE.test(normalize(code));
}

export function isCapitalGlobalCapitalizationVariant(code?: string): boolean {
  return NUMBERED_CAPITALIZATION.test(normalize(code));
}

/**
 * Vai no bloco "Assistências"/"Capitalização" (contêiner base ou variante)?
 * Quem não for isto é linha da tabela de coberturas — inclusive as coberturas
 * com níveis (`9300026`, `8200020`), que na seleção são linhas da tabela.
 */
export function isCapitalGlobalAssistanceOrCapitalization(
  code?: string
): boolean {
  return (
    isCapitalGlobalAssistanceUmbrella(code) ||
    isCapitalGlobalCapitalizationUmbrella(code) ||
    isCapitalGlobalAssistanceVariant(code) ||
    isCapitalGlobalCapitalizationVariant(code)
  );
}
